import {
  Modal,
  Button,
  Stack,
  Group,
  Text,
  Textarea,
  Badge,
  Divider,
  Card,
  Title,
  Alert,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconCheck, IconX } from '@tabler/icons-react';
import { useState } from 'react';
import { BidSubmission } from '../../../api/bidSubmissions';
import { useUpdateBidderQualification } from '../hooks/useBidders';
import { useTenderById } from '../hooks/useTenders';
import { formatCurrency } from '../../../utils/currency';
import dayjs from 'dayjs';

interface BidQualificationModalProps {
  opened: boolean;
  onClose: () => void;
  bidSubmission: BidSubmission | null;
  tenderId: string;
}

export function BidQualificationModal({
  opened,
  onClose,
  bidSubmission,
  tenderId,
}: BidQualificationModalProps) {
  const [qualificationStatus, setQualificationStatus] = useState<'QUALIFIED' | 'DISQUALIFIED' | null>(null);
  const updateQualification = useUpdateBidderQualification();
  const { data: tender } = useTenderById(tenderId);

  const form = useForm({
    initialValues: {
      qualificationNotes: bidSubmission?.qualificationNotes || '',
    },
    validate: {
      qualificationNotes: (value) => {
        if (qualificationStatus === 'DISQUALIFIED' && (!value || value.trim() === '')) {
          return 'Qualification notes are required when disqualifying a bidder';
        }
        return null;
      },
    },
  });

  const handleSubmit = async () => {
    if (!bidSubmission || !qualificationStatus || !bidSubmission.bidderId) {
      return;
    }

    if (qualificationStatus === 'DISQUALIFIED') {
      form.validate();
      if (form.errors.qualificationNotes) {
        return;
      }
    }

    try {
      await updateQualification.mutateAsync({
        tenderId,
        bidderId: bidSubmission.bidderId,
        qualificationStatus,
        qualificationNotes: qualificationStatus === 'DISQUALIFIED' ? form.values.qualificationNotes : undefined,
      });
      form.reset();
      setQualificationStatus(null);
      onClose();
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  const handleClose = () => {
    form.reset();
    setQualificationStatus(null);
    onClose();
  };

  if (!bidSubmission) {
    return null;
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Bid Review & Qualification"
      size="lg"
    >
      <Stack gap="md">
        {/* Bid Submission Details */}
        <Card shadow="sm" p="md" withBorder>
          <Title order={4} mb="md">Bid Submission Details</Title>
          <Stack gap="sm">
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Company Name</Text>
              <Text fw={500}>{bidSubmission.companyName}</Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Total Bid Price</Text>
              <Text fw={500}>{formatCurrency(Number(bidSubmission.totalPrice), tender?.currency || 'MVR')}</Text>
            </Group>
            {bidSubmission.representativeName && (
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Representative</Text>
                <Text>{bidSubmission.representativeName}</Text>
              </Group>
            )}
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Submitted At</Text>
              <Text>{dayjs(bidSubmission.submittedAt).format("MMM DD, YYYY HH:mm")}</Text>
            </Group>
            <Divider />
            <Group gap="xs">
              <Text size="sm" c="dimmed">Documents:</Text>
              {bidSubmission.hasTOR && <Badge size="xs" color="green">TOR</Badge>}
              {bidSubmission.hasFinancialStatement && <Badge size="xs" color="blue">Financial</Badge>}
              {bidSubmission.hasTaxClearance && <Badge size="xs" color="orange">Tax</Badge>}
              {bidSubmission.hasInsurance && <Badge size="xs" color="purple">Insurance</Badge>}
              {bidSubmission.hasExperience && <Badge size="xs" color="teal">Experience</Badge>}
              {bidSubmission.hasTechnicalProposal && <Badge size="xs" color="indigo">Technical</Badge>}
              {bidSubmission.hasFinancialProposal && <Badge size="xs" color="pink">Financial Prop</Badge>}
            </Group>
            {bidSubmission.qualificationStatus && (
              <>
                <Divider />
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Current Status</Text>
                  <Badge
                    color={
                      bidSubmission.qualificationStatus === 'QUALIFIED' ? 'green' :
                      bidSubmission.qualificationStatus === 'DISQUALIFIED' ? 'red' : 'gray'
                    }
                  >
                    {bidSubmission.qualificationStatus}
                  </Badge>
                </Group>
              </>
            )}
          </Stack>
        </Card>

        {/* Qualification Decision */}
        <Card shadow="sm" p="md" withBorder>
          <Title order={4} mb="md">Qualification Decision</Title>
          <Stack gap="md">
            <Group grow>
              <Button
                variant={qualificationStatus === 'QUALIFIED' ? 'filled' : 'light'}
                color="green"
                leftSection={<IconCheck size={16} />}
                onClick={() => setQualificationStatus('QUALIFIED')}
              >
                Qualify
              </Button>
              <Button
                variant={qualificationStatus === 'DISQUALIFIED' ? 'filled' : 'light'}
                color="red"
                leftSection={<IconX size={16} />}
                onClick={() => setQualificationStatus('DISQUALIFIED')}
              >
                Disqualify
              </Button>
            </Group>

            {qualificationStatus === 'DISQUALIFIED' && (
              <>
                <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
                  Qualification notes are required when disqualifying a bidder.
                </Alert>
                <Textarea
                  label="Qualification Notes"
                  placeholder="Please provide a reason for disqualification..."
                  required
                  rows={4}
                  {...form.getInputProps('qualificationNotes')}
                />
              </>
            )}

            {qualificationStatus === 'QUALIFIED' && (
              <Textarea
                label="Qualification Notes (Optional)"
                placeholder="Add any additional notes..."
                rows={4}
                {...form.getInputProps('qualificationNotes')}
              />
            )}
          </Stack>
        </Card>

        {/* Actions */}
        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!qualificationStatus || (qualificationStatus === 'DISQUALIFIED' && !form.values.qualificationNotes.trim())}
            loading={updateQualification.isPending}
            color={qualificationStatus === 'QUALIFIED' ? 'green' : 'red'}
          >
            {qualificationStatus === 'QUALIFIED' ? 'Qualify Bidder' : 'Disqualify Bidder'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

